import { useQuery } from "@apollo/client";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  CreateTagMutation,
  CreateTagMutationVariables,
  DeleteTagsMutation,
  DeleteTagsMutationVariables,
  EditTagInput,
  EditTagMutation,
  EditTagMutationVariables,
  ListTagsWithIntegrationsQuery,
  ListTagsWithIntegrationsQueryVariables,
  TagFilterInput,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { DeleteManyState, TableBox } from "~frontend/components/Table";
import {
  CREATE_TAG,
  DELETE_TAGS,
  EDIT_TAG,
  ListTagsWithIntegrationsTag,
  LIST_TAGS,
  LIST_TAGS_WITH_INTEGRATIONS,
} from "~frontend/graphql/tags";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import CreateTagDialog from "./CreateTagDialog";
import EditTagDialog from "./EditTagDialog";
import TagListControls from "./TagListControls";
import TagTable from "./TagTable";
import { TAG_SORTS } from "./constants";

export default function Tags(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const snackbar = useSnackbar();
  const confirm = useConfirm();
  useBreadcrumbs([{ label: "Tags", path: subaccountLink("/tags") }]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, TAG_SORTS[0].value)
  );

  const filter: TagFilterInput = search ? { nameContains: search } : {};

  const sortValue =
    TAG_SORTS.find(availableSort => availableSort.value === sort)?.value ||
    TAG_SORTS[0].value;

  const listTagVariables = {
    input: {
      limit: pageSize,
      offset: page * pageSize,
      filter,
      sort: sortValue,
      subaccountId: subaccount.id,
    },
  };

  const listTagsResult = useQuery<
    ListTagsWithIntegrationsQuery,
    ListTagsWithIntegrationsQueryVariables
  >(LIST_TAGS_WITH_INTEGRATIONS, { variables: listTagVariables });

  const tags = listTagsResult.data?.listTags.tags;

  const [createTagDialogOpen, setCreateTagDialogOpen] = useState(false);

  const [createTag] = useMutation<
    CreateTagMutation,
    CreateTagMutationVariables
  >(CREATE_TAG, {
    refetchQueries: [{ query: LIST_TAGS, variables: listTagVariables }],
    awaitRefetchQueries: true,
  });

  const handleCreateTag = useCallback(
    async ({ name }: { name: string }) => {
      const { errors } = await createTag({
        variables: { input: { name, subaccountId: subaccount.id } },
      });

      if (errors) {
        snackbar.enqueueSnackbar(`Error creating tag: ${errors[0].message}`, {
          variant: "error",
        });
        throw errors;
      } else {
        snackbar.enqueueSnackbar(`Tag ${name} created`);
      }
    },
    [createTag, subaccount, snackbar]
  );

  const [editingTagId, setEditingTagId] = useState<string | null>(null);

  const editingTag = useMemo<ListTagsWithIntegrationsTag | null>(
    () => tags?.find(tag => tag.id === editingTagId) ?? null,
    [tags, editingTagId]
  );

  const [editTag] = useMutation<EditTagMutation, EditTagMutationVariables>(
    EDIT_TAG,
    {
      refetchQueries: [
        { query: LIST_TAGS_WITH_INTEGRATIONS, variables: listTagVariables },
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleEditTag = useCallback(
    async (input: EditTagInput) => {
      if (!editingTag) {
        console.warn("Not editing a tag");
        return;
      }

      const { errors } = await editTag({
        variables: { input },
      });

      if (errors) {
        snackbar.enqueueSnackbar(`Error editing tag: ${errors[0].message}`, {
          variant: "error",
        });
        throw errors;
      } else {
        snackbar.enqueueSnackbar(`Tag ${name} updated`);
        setEditingTagId(null);
      }
    },
    [editingTag, setEditingTagId, editTag, snackbar]
  );

  const [deleteTags] = useMutation<
    DeleteTagsMutation,
    DeleteTagsMutationVariables
  >(DELETE_TAGS, {
    refetchQueries: [{ query: LIST_TAGS, variables: listTagVariables }],
    awaitRefetchQueries: true,
  });

  const handleDeleteTag = useCallback(
    async (id: string) => {
      const tag = tags?.find(tag => tag.id === id);
      if (!tag) {
        return;
      }

      const { errors } = await deleteTags({
        variables: { input: { tagIds: [tag.id] } },
      });

      if (errors) {
        snackbar.enqueueSnackbar(`Error deleting tag: ${errors[0].message}`, {
          variant: "error",
        });
        throw errors;
      } else {
        snackbar.enqueueSnackbar(`Tag ${tag.name} deleted`);
      }
    },
    [tags, deleteTags, snackbar]
  );

  const [deleteManyState, setDeleteManyState] = useState<DeleteManyState>({
    selecting: false,
  });

  const resetDeleteManyState = useCallback(
    () => setDeleteManyState({ selecting: false }),
    [setDeleteManyState]
  );

  // clear delete many selections when tags update
  useEffect(resetDeleteManyState, [tags, resetDeleteManyState]);

  const handleDeleteSelectedTags = useCallback(async () => {
    if (!deleteManyState.selecting) {
      return;
    }
    const selectedTags = tags?.filter(tag =>
      deleteManyState.selectedIds.includes(tag.id)
    );
    if (!selectedTags?.length) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Tags",
      content: `Are you sure you want to delete the following tags: ${selectedTags
        .map(tag => `"${tag.name}"`)
        .join(", ")}?`,
    });
    if (!confirmed) {
      return;
    }

    const { errors } = await deleteTags({
      variables: { input: { tagIds: selectedTags.map(tag => tag.id) } },
    });

    if (errors) {
      snackbar.enqueueSnackbar(`Error deleting tags: ${errors[0].message}`, {
        variant: "error",
      });
      throw errors;
    } else {
      resetDeleteManyState();
      snackbar.enqueueSnackbar(
        `${plural(selectedTags.length, "tag", "tags")} deleted`
      );
    }
  }, [
    deleteManyState,
    resetDeleteManyState,
    tags,
    confirm,
    deleteTags,
    snackbar,
  ]);

  return (
    <PageContainer>
      <PageHeader>Tags</PageHeader>
      <TagListControls
        sort={sortValue}
        setSort={setSort}
        search={search}
        setSearch={newSearch => {
          setSearch(newSearch);
          setPage(0);
        }}
        onClickAdd={() => setCreateTagDialogOpen(true)}
        deleteManyState={deleteManyState}
        setDeleteManyState={setDeleteManyState}
        onDeleteSelected={handleDeleteSelectedTags}
      />
      <TableBox>
        <AsyncWrapper
          loading={listTagsResult.loading}
          errors={listTagsResult.error}
          data={listTagsResult.data}
        >
          {({ listTags }) => (
            <TagTable
              rows={listTags.tags}
              totalCount={listTags.totalCount}
              page={page}
              onChangePage={setPage}
              rowsPerPage={pageSize}
              onChangeRowsPerPage={newPageSize => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              onClickEdit={setEditingTagId}
              onClickDelete={handleDeleteTag}
              deleteManyState={deleteManyState}
              onChangeDeleteManyState={setDeleteManyState}
            />
          )}
        </AsyncWrapper>
      </TableBox>
      <CreateTagDialog
        open={createTagDialogOpen}
        onSubmit={handleCreateTag}
        onClose={() => setCreateTagDialogOpen(false)}
      />
      {editingTag ? (
        <EditTagDialog
          open
          tag={editingTag}
          onSubmit={handleEditTag}
          onClose={() => setEditingTagId(null)}
        />
      ) : (
        <></>
      )}
    </PageContainer>
  );
}
